export const validators = {
  // Email validation
  email: (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) return 'Email is required';
    if (!emailRegex.test(value)) return 'Invalid email format';
    return null;
  },

  // Password validation
  password: (value, minLength = 6) => {
    if (!value) return 'Password is required';
    if (value.length < minLength) return `Password must be at least ${minLength} characters`;
    return null;
  },

  // Confirm password
  confirmPassword: (value, password) => {
    if (!value) return 'Please confirm your password';
    if (value !== password) return 'Passwords do not match';
    return null;
  },

  // Phone validation (Indian)
  phone: (value, required = false) => {
    if (!required && !value) return null;
    if (required && !value) return 'Phone number is required';
    
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(value)) {
      return 'Invalid phone number (must be 10 digits starting with 6-9)';
    }
    return null;
  },

  // Name validation
  name: (value, fieldName = 'Name') => {
    if (!value) return `${fieldName} is required`;
    if (value.length < 2) return `${fieldName} must be at least 2 characters`;
    if (value.length > 50) return `${fieldName} must not exceed 50 characters`;
    return null;
  },

  // Required field
  required: (value, fieldName = 'This field') => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${fieldName} is required`;
    }
    return null;
  },

  // Aadhaar validation
  aadhaar: (value) => {
    if (!value) return 'Aadhaar number is required';
    const aadhaarRegex = /^\d{12}$/;
    if (!aadhaarRegex.test(value)) {
      return 'Aadhaar must be exactly 12 digits';
    }
    return null;
  },

  // PAN validation
  pan: (value) => {
    if (!value) return 'PAN number is required';
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(value.toUpperCase())) {
      return 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    return null;
  },

  // GST validation
  gst: (value, required = false) => {
    if (!required && !value) return null;
    if (required && !value) return 'GST number is required';
    
    const gstRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d[Z]{1}[A-Z\d]{1}$/;
    if (!gstRegex.test(value.toUpperCase())) {
      return 'Invalid GST format (e.g., 22ABCDE1234F1Z5)';
    }
    return null;
  },

  // IFSC validation
  ifsc: (value) => {
    if (!value) return 'IFSC code is required';
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(value.toUpperCase())) {
      return 'Invalid IFSC format (e.g., SBIN0001234)';
    }
    return null;
  },

  // Pincode validation
  pincode: (value) => {
    if (!value) return 'Pincode is required';
    const pincodeRegex = /^\d{6}$/;
    if (!pincodeRegex.test(value)) {
      return 'Pincode must be exactly 6 digits';
    }
    return null;
  },

  // Bank account number
  accountNumber: (value) => {
    if (!value) return 'Account number is required';
    if (!/^\d{9,18}$/.test(value)) {
      return 'Account number must be 9-18 digits';
    }
    return null;
  },

  // File validation
  file: (file, maxSize = 5 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']) => {
    if (!file) return 'File is required';
    
    if (file.size > maxSize) {
      return `File size must not exceed ${maxSize / (1024 * 1024)}MB`;
    }
    
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only JPG, PNG, and PDF are allowed';
    }
    
    return null;
  },

  // OTP validation
  otp: (value, length = 6) => {
    if (!value) return 'OTP is required';
    if (!/^\d+$/.test(value)) return 'OTP must contain only digits';
    if (value.length !== length) return `OTP must be exactly ${length} digits`;
    return null;
  },
};

// Helper to validate entire form
export const validateForm = (values, rules) => {
  const errors = {};
  
  Object.keys(rules).forEach(field => {
    const rule = rules[field];
    const value = values[field];
    
    if (typeof rule === 'function') {
      const error = rule(value);
      if (error) errors[field] = error;
    } else if (Array.isArray(rule)) {
      // Multiple validators for one field
      for (const validator of rule) {
        const error = validator(value);
        if (error) {
          errors[field] = error;
          break;
        }
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export default validators;